/* eslint-disable */
import * as React from 'react';
import {
  Badge, Box, Button, Collapse, Flex, FormControl, FormLabel, HStack, Icon, IconButton,
  Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalOverlay,
  SimpleGrid, Stack, Table, Tbody, Td, Text, Th, Thead, Tooltip,
  Tr, useColorModeValue, useDisclosure, useToast,
} from '@chakra-ui/react';
import {
  ColumnFiltersState, PaginationState, createColumnHelper, flexRender,
  getCoreRowModel, getFacetedMinMaxValues, getFacetedRowModel, getFacetedUniqueValues,
  getFilteredRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import {
  MdAddPhotoAlternate, MdCancel, MdChevronLeft, MdChevronRight, MdDoneAll,
  MdEvent, MdFilterList, MdPhotoCamera, MdPhotoLibrary, MdRefresh,
} from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { getSessionCandidateId } from 'helpers/session';

type RowObj = {
  id: number; Actividad: string; Atendido: boolean;
  FechaEvento: string; Descripcion: string; Ubicacion: string;
};

type EvidenceItem = {
  Imagen?: string;
};

type AtendidoFilter = 'Todos' | 'Atendido' | 'Pendiente';

const inputStyle = {
  variant: 'filled' as const,
  borderRadius: 'lg',
  _focus: { bg: 'white', borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' },
};

export default function SearchTableOrders(props: { tableData: any }) {
  const { tableData } = props;
  const toast = useToast();
  const filterBg = useColorModeValue('gray.50', 'navy.900');
  const textColor = useColorModeValue('navy.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const brandColor = useColorModeValue('brand.500', 'brand.400');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');

  // evidence upload modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [orden, setOrden] = React.useState(0);
  const [file2, setFile2] = React.useState<File | null>(null);
  const [textFile2, setTextFile2] = React.useState('');
  const inputFileRefevi2 = React.useRef<HTMLInputElement>(null);
  const { isOpen: isOpenGaleria, onOpen: onOpenGaleria, onClose: onCloseGaleria } = useDisclosure();
  const [galleryImages, setGalleryImages] = React.useState<EvidenceItem[]>([]);
  const [galleryEvent, setGalleryEvent] = React.useState<RowObj | null>(null);
  const { isOpen: isOpenImagen, onOpen: onOpenImagen, onClose: onCloseImagen } = useDisclosure();
  const [expandedImage, setExpandedImage] = React.useState<string>('');

  // nueva actividad modal
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();
  const [showFilters, setShowFilters] = React.useState(false);
  const [atendidoFilter, setAtendidoFilter] = React.useState<AtendidoFilter>('Todos');
  const [fechaDesde, setFechaDesde] = React.useState('');
  const [fechaHasta, setFechaHasta] = React.useState('');

  const activeFilters = [atendidoFilter !== 'Todos', !!(fechaDesde || fechaHasta)].filter(Boolean).length;
  const resetFilters = () => { setAtendidoFilter('Todos'); setFechaDesde(''); setFechaHasta(''); };

  const [data, setData] = React.useState<RowObj[]>(() => [...tableData]);
  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [{ pageIndex, pageSize }, setPagination] = React.useState<PaginationState>({ pageIndex: 0, pageSize: 8 });
  const pagination = React.useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

  const filteredData = React.useMemo(() => data.filter((r) => {
    if (atendidoFilter === 'Atendido' && !r.Atendido) return false;
    if (atendidoFilter === 'Pendiente' && r.Atendido) return false;
    if (fechaDesde || fechaHasta) {
      const d = r.FechaEvento ? new Date(r.FechaEvento) : null;
      if (!d) return false;
      if (fechaDesde && d < new Date(fechaDesde)) return false;
      if (fechaHasta && d > new Date(fechaHasta + 'T23:59:59')) return false;
    }
    return true;
  }), [data, atendidoFilter, fechaDesde, fechaHasta]);

  const counts = React.useMemo(() => ({
    total: data.length,
    atendidos: data.filter(r => r.Atendido).length,
    pendientes: data.filter(r => !r.Atendido).length,
  }), [data]);

  const { handleSubmit, register, reset, formState: { isSubmitting } } = useForm();

  const refreshTable = async () => {
    const candidateId = getSessionCandidateId();
    const url = candidateId
      ? `${process.env.REACT_APP_URL_SERVER}/agenda?idCandidato=${candidateId}`
      : process.env.REACT_APP_URL_SERVER + '/agenda';
    const d = await fetch(url, { credentials: 'include' }).then(r => r.json()).catch(() => null);
    if (d) setData(d);
  };

  const openGallery = async (row: RowObj) => {
    try {
      const evidencias = await fetch(process.env.REACT_APP_URL_SERVER + '/evidencias', { credentials: 'include' })
        .then((response) => response.json())
        .catch(() => []);
      const eventWithEvidence = Array.isArray(evidencias)
        ? evidencias.find((item) => item?.id === row.id)
        : null;
      setGalleryImages(Array.isArray(eventWithEvidence?.Evidencias) ? eventWithEvidence.Evidencias : []);
      setGalleryEvent(row);
      onOpenGaleria();
    } catch (error) {
      toast({ title: 'No fue posible abrir la galeria', status: 'error', duration: 2500 });
    }
  };

  const openExpandedImage = (image?: string) => {
    if (!image) return;
    setExpandedImage(image);
    onOpenImagen();
  };

  const onSubmitNuevo = async (values: any) => {
    const candidateId = getSessionCandidateId();
    if (!candidateId) {
      toast({ title: 'No se encontro idCandidato', status: 'error', duration: 3000 });
      return;
    }

    const fd = new FormData();
    fd.append('Nombre', values.Actividad);
    fd.append('Descripcion', values.Descripcion || '');
    fd.append('Fecha', values.FechaEvento);
    fd.append('Ubicacion', values.Ubicacion || '');
    fd.append('CorreosE', values.CorreosAdicionales || '');
    fd.append('idCandidato', String(candidateId));
    const res = await fetch(process.env.REACT_APP_URL_SERVER + '/NewEvent', { method: 'POST', body: fd, credentials: 'include' });
    if (res.ok) {
      toast({ title: 'Actividad registrada', status: 'success', duration: 3000 });
      reset(); onCloseNuevo(); await refreshTable();
    } else {
      toast({ title: 'Error al guardar', description: await res.text(), status: 'error', duration: 3000 });
    }
  };

  const columnHelper = createColumnHelper<RowObj>();
  const columns = [
    columnHelper.accessor('Actividad', {
      id: 'actividad',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ACTIVIDAD</Text>,
      cell: (info) => (
        <Box>
          <Text color={textColor} fontWeight="600" fontSize="sm">{info.getValue()}</Text>
          <Text color="gray.400" fontSize="xs" noOfLines={1}>{info.row.original.Descripcion}</Text>
        </Box>
      ),
    }),
    columnHelper.accessor('Ubicacion', {
      id: 'ubicacion',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">UBICACION</Text>,
      cell: (info) => <Text color={textColor} fontSize="sm">{info.getValue()}</Text>,
    }),
    columnHelper.accessor('FechaEvento', {
      id: 'fecha',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">FECHA / HORA</Text>,
      cell: (info) => {
        const v = info.getValue();
        return <Text color="gray.500" fontSize="xs">{v ? new Date(v).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</Text>;
      },
    }),
    columnHelper.accessor('Atendido', {
      id: 'estatus',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ESTATUS</Text>,
      cell: (info) => (
        <Badge colorScheme={info.getValue() ? 'green' : 'red'} fontSize="xs" borderRadius="full" px="3" py="1">
          {info.getValue() ? 'Atendido' : 'Sin Atender'}
        </Badge>
      ),
    }),
    columnHelper.accessor('id', {
      id: 'actions',
      header: () => <Text fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" fontWeight="700">ACCIONES</Text>,
      cell: (info) => (
        <HStack spacing="2px">
          <Tooltip label="Marcar atendido">
            <IconButton
              aria-label="atender"
              size="sm"
              variant="ghost"
              colorScheme="green"
              icon={<Icon as={MdDoneAll} h="18px" w="18px" />}
              onClick={async () => {
                const fd = new FormData(); fd.append('id', info.getValue().toString()); fd.append('Atendido', 'true');
                await fetch(process.env.REACT_APP_URL_SERVER + '/agenda_mod', { method: 'POST', body: fd, credentials: 'include' });
                await refreshTable();
                toast({ title: 'Actividad atendida', status: 'success', duration: 2000 });
              }}
            />
          </Tooltip>
          <Tooltip label="Marcar no atendido">
            <IconButton
              aria-label="cancelar"
              size="sm"
              variant="ghost"
              colorScheme="red"
              icon={<Icon as={MdCancel} h="18px" w="18px" />}
              onClick={async () => {
                const fd = new FormData(); fd.append('id', info.getValue().toString()); fd.append('Atendido', 'false');
                await fetch(process.env.REACT_APP_URL_SERVER + '/agenda_mod', { method: 'POST', body: fd, credentials: 'include' });
                await refreshTable();
                toast({ title: 'Actividad marcada sin atender', status: 'success', duration: 2000 });
              }}
            />
          </Tooltip>
          <Tooltip label="Subir evidencia">
            <IconButton
              aria-label="evidencia"
              size="sm"
              variant="ghost"
              colorScheme="gray"
              icon={<Icon as={MdPhotoCamera} h="18px" w="18px" />}
              onClick={() => { setOrden(info.getValue()); onOpen(); }}
            />
          </Tooltip>
          <Tooltip label="Ver galeria">
            <IconButton
              aria-label="galeria"
              size="sm"
              variant="ghost"
              colorScheme="purple"
              icon={<Icon as={MdPhotoLibrary} h="18px" w="18px" />}
              onClick={() => openGallery(info.row.original)}
            />
          </Tooltip>
        </HStack>
      ),
    }),
  ];

  const table = useReactTable({
    data: filteredData, columns,
    state: { columnFilters, globalFilter, pagination },
    onPaginationChange: setPagination, onColumnFiltersChange: setColumnFilters, onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(), getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(), getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(), getFacetedUniqueValues: getFacetedUniqueValues(), getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });
  const createPages = (n: number) => Array.from({ length: n }, (_, i) => i + 1);

  return (
    <Flex direction="column" w="100%" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
      <Flex align="center" justify="space-between" w="100%" px="22px" mb="16px" flexWrap="wrap" gap="10px">
        <DebouncedInput value={globalFilter ?? ''} onChange={(v) => setGlobalFilter(String(v))} placeholder="Buscar actividad..." />
        <HStack spacing="8px">
          <Tooltip label="Actualizar">
            <IconButton aria-label="refresh" size="md" variant="outline" icon={<Icon as={MdRefresh} h="18px" w="18px" />} onClick={refreshTable} />
          </Tooltip>
          <Button variant="outline" fontSize="sm" leftIcon={<Icon as={MdFilterList} />} onClick={() => setShowFilters(v => !v)} position="relative">
            Filtros
            {activeFilters > 0 && (
              <Badge colorScheme="brand" borderRadius="full" position="absolute" top="-6px" right="-6px" fontSize="10px" minW="18px" h="18px" display="flex" alignItems="center" justifyContent="center">{activeFilters}</Badge>
            )}
          </Button>
          <Button variant="brand" fontSize="sm" fontWeight="500" onClick={onOpenNuevo}>+ Nueva Actividad</Button>
        </HStack>
      </Flex>

      {/* Summary chips */}
      <Flex px="22px" mb="12px" gap="8px" flexWrap="wrap">
        {[
          { label: 'Todos', val: 'Todos' as const, count: counts.total, color: 'gray' },
          { label: 'Atendido', val: 'Atendido' as const, count: counts.atendidos, color: 'green' },
          { label: 'Sin Atender', val: 'Pendiente' as const, count: counts.pendientes, color: 'red' },
        ].map(f => (
          <Badge
            key={f.val}
            colorScheme={atendidoFilter === f.val ? f.color : 'gray'}
            variant={atendidoFilter === f.val ? 'solid' : 'outline'}
            fontSize="xs" px="3" py="1" borderRadius="full" cursor="pointer"
            onClick={() => { setAtendidoFilter(f.val); setPagination(p => ({ ...p, pageIndex: 0 })); }}
            _hover={{ opacity: 0.8 }} userSelect="none"
          >
            {f.label} ({f.count})
          </Badge>
        ))}
        {activeFilters > 0 && (
          <Badge colorScheme="orange" variant="outline" fontSize="xs" px="3" py="1" borderRadius="full" cursor="pointer" onClick={resetFilters} _hover={{ opacity: 0.8 }}>✕ Limpiar</Badge>
        )}
      </Flex>

      <Collapse in={showFilters} animateOpacity>
        <Box mx="22px" mb="16px" p="16px" bg={filterBg} borderRadius="12px" border="1px solid" borderColor={borderColor}>
          <Text fontSize="sm" fontWeight="700" color={textColor} mb="14px">Filtros</Text>
          <Box>
            <FormLabel fontSize="xs" fontWeight="600" color="gray.500" mb="6px">RANGO DE FECHA DE EVENTO</FormLabel>
            <HStack spacing="8px">
              <Input size="sm" type="date" borderRadius="8px" value={fechaDesde} onChange={(e) => { setFechaDesde(e.target.value); setPagination(p => ({ ...p, pageIndex: 0 })); }} />
              <Text fontSize="xs" color="gray.400">al</Text>
              <Input size="sm" type="date" borderRadius="8px" value={fechaHasta} onChange={(e) => { setFechaHasta(e.target.value); setPagination(p => ({ ...p, pageIndex: 0 })); }} />
            </HStack>
          </Box>
          <Flex mt="14px" justify="flex-end">
            <Button size="sm" variant="ghost" colorScheme="red" onClick={resetFilters}>Limpiar</Button>
          </Flex>
        </Box>
      </Collapse>
      {activeFilters > 0 && (
        <Text px="22px" mb="8px" fontSize="xs" color="gray.500">Mostrando <strong>{filteredData.length}</strong> de {data.length}</Text>
      )}

      {/* Evidence upload modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" scrollBehavior="inside" motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
          <Box bg="green.500" px={6} py={4}>
            <HStack justify="space-between" align="center">
              <HStack gap={3}>
                <Icon as={MdAddPhotoAlternate} color="white" w={5} h={5} />
                <Text color="white" fontWeight="700" fontSize="lg">Subir Evidencia — Evento #{orden}</Text>
              </HStack>
              <ModalCloseButton position="static" color="white" _hover={{ bg: 'whiteAlpha.300' }} borderRadius="lg" />
            </HStack>
          </Box>
          <ModalBody px={6} py={5}>
            <FField label="Imagen de Evidencia">
              <HStack>
                <Button
                  size="sm"
                  variant="outline"
                  borderRadius="lg"
                  leftIcon={<Icon as={MdAddPhotoAlternate} />}
                  onClick={() => inputFileRefevi2.current?.click()}
                >
                  Seleccionar imagen
                </Button>
                <Text fontSize="sm" color="gray.500">{textFile2 || 'Sin archivo'}</Text>
              </HStack>
              <input
                ref={inputFileRefevi2}
                type="file"
                accept=".jpg,.jpeg,.png"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) { setFile2(f); setTextFile2(f.name); }
                }}
              />
            </FField>
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3} px={6} py={4}>
            <Button variant="ghost" borderRadius="xl" onClick={onClose}>Cerrar</Button>
            <Button
              colorScheme="green"
              borderRadius="xl"
              px={8}
              onClick={async () => {
                const fd = new FormData();
                fd.append('id', orden.toString());
                if (file2) fd.append('Evidencia', file2);
                const loadingToast = toast({ title: 'Subiendo...', status: 'loading' });
                await fetch(process.env.REACT_APP_URL_SERVER + '/subir_evi', { method: 'POST', body: fd, credentials: 'include' });
                toast.close(loadingToast);
                toast({ title: 'Evidencia subida', status: 'success', duration: 2000 });
                onClose(); setFile2(null); setTextFile2('');
              }}
            >
              Subir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenGaleria} onClose={onCloseGaleria} size="5xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
          <Box bg="purple.600" px={6} py={4}>
            <HStack justify="space-between" align="center">
              <Stack spacing={0}>
                <Text color="white" fontWeight="700" fontSize="lg">Ver Galeria</Text>
                <Text color="whiteAlpha.800" fontSize="sm">
                  {galleryEvent ? `${galleryEvent.Actividad} · Evento #${galleryEvent.id}` : 'Galeria de evidencias'}
                </Text>
              </Stack>
              <ModalCloseButton position="static" color="white" _hover={{ bg: 'whiteAlpha.300' }} borderRadius="lg" />
            </HStack>
          </Box>
          <ModalBody px={6} py={6}>
            {galleryImages.length === 0 ? (
              <Flex minH="220px" align="center" justify="center">
                <Text color="gray.500">Este evento todavia no tiene evidencias cargadas.</Text>
              </Flex>
            ) : (
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
                {galleryImages.map((item, index) => (
                  <Box
                    key={`${item.Imagen || 'img'}-${index}`}
                    cursor={item.Imagen ? 'zoom-in' : 'default'}
                    borderRadius="22px"
                    overflow="hidden"
                    border="1px solid"
                    borderColor="gray.100"
                    bg="gray.50"
                    minH="220px"
                    backgroundImage={item.Imagen}
                    backgroundPosition="center"
                    backgroundRepeat="no-repeat"
                    backgroundSize="cover"
                    onClick={() => openExpandedImage(item.Imagen)}
                  />
                ))}
              </SimpleGrid>
            )}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3}>
            {galleryEvent ? (
              <Button
                colorScheme="blue"
                onClick={() => window.location.replace(process.env.REACT_APP_URL_SERVER + '/descargar_imagenes/' + galleryEvent.id)}
                isDisabled={galleryImages.length === 0}
              >
                Descargar Imagenes
              </Button>
            ) : null}
            <Button variant="ghost" onClick={onCloseGaleria}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenImagen} onClose={onCloseImagen} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw">
          <ModalCloseButton color="white" zIndex={2} />
          <ModalBody p={0}>
            <Flex align="center" justify="center" minH={{ base: '60vh', md: '75vh' }}>
              <Box
                as="img"
                src={expandedImage}
                alt="Evidencia ampliada"
                maxW="100%"
                maxH="80vh"
                objectFit="contain"
                borderRadius="20px"
                boxShadow="0 20px 70px rgba(0, 0, 0, 0.45)"
              />
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Nueva Actividad modal */}
      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="2xl" scrollBehavior="inside" motionPreset="slideInBottom">
        <ModalOverlay backdropFilter="blur(4px)" bg="blackAlpha.400" />
        <ModalContent borderRadius="2xl" overflow="hidden" mx={4}>
          <Box bg="brand.500" px={6} py={4}>
            <HStack justify="space-between" align="center">
              <HStack gap={3}>
                <Icon as={MdEvent} color="white" w={5} h={5} />
                <Text color="white" fontWeight="700" fontSize="lg">Registrar Nueva Actividad</Text>
              </HStack>
              <ModalCloseButton position="static" color="white" _hover={{ bg: 'whiteAlpha.300' }} borderRadius="lg" />
            </HStack>
          </Box>
          <form onSubmit={handleSubmit(onSubmitNuevo)}>
            <ModalBody px={6} py={5}>
              <Stack gap="14px">
                <FField label="Nombre del Evento">
                  <Input placeholder="Mitin Centro" {...register('Actividad', { required: true })} {...inputStyle} />
                </FField>
                <FField label="Descripcion">
                  <Input placeholder="Presentacion de propuestas..." {...register('Descripcion')} {...inputStyle} />
                </FField>
                <FField label="Fecha y Hora del Evento">
                  <Input type="datetime-local" {...register('FechaEvento', { required: true })} {...inputStyle} />
                </FField>
                <FField label="Ubicacion">
                  <Input placeholder="Av. Juarez 123, Centro" {...register('Ubicacion')} {...inputStyle} />
                </FField>
                <FField label="Correos Adicionales">
                  <Input placeholder="extra1@correo.com, extra2@correo.com" {...register('CorreosAdicionales')} {...inputStyle} />
                </FField>
              </Stack>
            </ModalBody>
            <ModalFooter borderTop="1px solid" borderColor="gray.100" gap={3} px={6} py={4}>
              <Button variant="ghost" borderRadius="xl" onClick={onCloseNuevo}>Cancelar</Button>
              <Button colorScheme="brand" type="submit" isLoading={isSubmitting} borderRadius="xl" px={8}>Guardar</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <Table variant="simple" color="gray.500" mb="24px">
        <Thead>
          {table.getHeaderGroups().map(hg => (
            <Tr key={hg.id}>
              {hg.headers.map(h => (
                <Th pe="10px" borderColor={borderColor} key={h.id} colSpan={h.colSpan}>
                  {h.isPlaceholder ? null : (
                    <Flex justify="space-between" align="center" fontSize={{ sm: '10px', lg: '12px' }} color="gray.400" cursor={h.column.getCanSort() ? 'pointer' : 'default'} onClick={h.column.getToggleSortingHandler()}>
                      {flexRender(h.column.columnDef.header, h.getContext())}
                    </Flex>
                  )}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.length === 0
            ? (<Tr><Td colSpan={columns.length} textAlign="center" py="40px" color="gray.400">Sin registros</Td></Tr>)
            : table.getRowModel().rows.map(row => (
                <Tr key={row.id} _hover={{ bg: rowHover }}>
                  {row.getVisibleCells().map(cell => (
                    <Td key={cell.id} fontSize={{ sm: '14px' }} minW={{ sm: '120px', md: 'auto' }} borderColor={borderColor} py="12px">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </Td>
                  ))}
                </Tr>
              ))}
        </Tbody>
      </Table>

      <Flex w="100%" justify="space-between" px="20px" pt="10px" pb="5px">
        <Text fontSize="sm" color="gray.500" mb={{ sm: '24px', md: '0px' }}>
          {filteredData.length === 0 ? 'Sin registros' : `${pageSize * pageIndex + 1}–${Math.min(pageSize * (pageIndex + 1), filteredData.length)} de ${filteredData.length}`}
        </Text>
        <Stack direction="row" alignSelf="flex-end" spacing="4px" ms="auto">
          <Button variant="no-effects" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()} w="40px" h="40px" borderRadius="50%" bg="transparent" border="1px solid" borderColor={borderColor} display={table.getCanPreviousPage() ? 'flex' : 'none'} _hover={{ opacity: '0.7' }}>
            <Icon as={MdChevronLeft} w="16px" h="16px" color={textColor} />
          </Button>
          {createPages(table.getPageCount()).map((p, i) => (
            <Button key={i} variant="no-effects" onClick={() => table.setPageIndex(p - 1)} w="40px" h="40px" borderRadius="50%" bg={p === pageIndex + 1 ? brandColor : 'transparent'} border={p === pageIndex + 1 ? 'none' : '1px solid lightgray'} _hover={p === pageIndex + 1 ? { opacity: '0.7' } : { bg: 'whiteAlpha.100' }}>
              <Text fontSize="sm" color={p === pageIndex + 1 ? '#fff' : textColor}>{p}</Text>
            </Button>
          ))}
          <Button variant="no-effects" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()} w="40px" h="40px" borderRadius="50%" bg="transparent" border="1px solid" borderColor={borderColor} display={table.getCanNextPage() ? 'flex' : 'none'} _hover={{ opacity: '0.7' }}>
            <Icon as={MdChevronRight} w="16px" h="16px" color={textColor} />
          </Button>
        </Stack>
      </Flex>
    </Flex>
  );
}

function FField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <FormControl>
      <FormLabel fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="wide" color="gray.500" mb={1}>{label}</FormLabel>
      {children}
    </FormControl>
  );
}

function DebouncedInput({ value: iv, onChange, debounce = 500, ...props }: { value: string | number; onChange: (v: string | number) => void; debounce?: number } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(iv);
  React.useEffect(() => { setValue(iv); }, [iv]);
  React.useEffect(() => { const t = setTimeout(() => onChange(value), debounce); return () => clearTimeout(t); }, [value]);
  return <SearchBar {...props} value={value} onChange={(e: any) => setValue(e.target.value)} h="44px" w={{ lg: '390px' }} borderRadius="16px" />;
}
